import { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Loader2, Mail, Lock, Store } from "lucide-react";
import { toast } from "sonner";
import Logo from "../components/Logo";
import api, { fmtErr } from "../lib/api";
import { useAuth } from "../context/AuthContext";

export default function FranchiseLogin() {
  const { setUser, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [form, setForm] = useState({ email: "", password: "" });

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      const { data } = await api.post("/auth/login", form);
      if (data.role !== "franchise") {
        await logout().catch(() => {});
        setUser(null);
        setError("This portal is for ShipAny franchise partners. Customers sign in at shipany.in/login.");
        return;
      }
      if (data.must_change_password) {
        setUser(data);
        toast.info("Temporary password accepted — set your new password to continue");
        navigate("/set-password", { replace: true });
        return;
      }
      setUser(data);
      const from = location.state?.from;
      navigate(from && String(from).startsWith("/franchise") ? from : "/franchise", { replace: true });
    } catch (err) {
      setError(fmtErr(err, "Invalid credentials"));
    } finally {
      setLoading(false);
    }
  };

  const inputCls = "w-full rounded-xl border border-white/15 bg-white/5 py-3 pl-10 pr-4 text-sm text-white placeholder:text-slate-500 focus:border-shipany-cyan focus:outline-none focus:ring-2 focus:ring-shipany-cyan/20";

  return (
    <div className="relative flex min-h-screen items-center justify-center overflow-hidden bg-shipany-navy px-5" data-testid="franchise-login-page">
      <div className="pointer-events-none absolute inset-0 hero-grid opacity-40" />
      <div className="pointer-events-none absolute -left-32 top-0 h-96 w-96 rounded-full bg-shipany-cyan/10 blur-3xl" />
      <div className="pointer-events-none absolute -bottom-32 -right-32 h-96 w-96 rounded-full bg-[#FF9933]/10 blur-3xl" />

      <div className="relative w-full max-w-md">
        <div className="flex justify-center"><Logo dark /></div>
        <div className="mt-8 rounded-2xl border border-white/10 bg-white/[0.05] p-8 shadow-2xl shadow-black/40 backdrop-blur-xl" data-testid="franchise-login-card">
          <div className="flex items-center gap-3">
            <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-gradient-to-br from-shipany-blue/50 to-shipany-cyan/25">
              <Store className="h-5 w-5 text-shipany-cyan" />
            </span>
            <div>
              <h1 className="font-display text-xl font-bold text-white">ShipAny Franchise</h1>
              <p className="text-xs text-slate-400">Partner portal — franchise accounts only.</p>
            </div>
          </div>

          <form onSubmit={submit} className="mt-6 space-y-3.5">
            <div className="relative">
              <Mail className="absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-500" />
              <input data-testid="franchise-login-email-input" type="email" placeholder="Franchise email address" required
                value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className={inputCls} />
            </div>
            <div className="relative">
              <Lock className="absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-500" />
              <input data-testid="franchise-login-password-input" type="password" placeholder="Password" required
                value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className={inputCls} />
            </div>
            {error && <p className="rounded-lg border border-red-400/30 bg-red-500/10 px-3 py-2 text-xs font-medium text-red-300" data-testid="franchise-login-error">{error}</p>}
            <button data-testid="franchise-login-submit-btn" disabled={loading}
              className="flex w-full items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-[#FF9933] to-[#FF8000] py-3 text-sm font-bold text-[#071B3A] transition-all hover:shadow-[0_8px_30px_rgba(255,153,51,0.4)] disabled:opacity-60">
              {loading && <Loader2 className="h-4 w-4 animate-spin" />}
              Sign In to Franchise Portal
            </button>
          </form>

          <p className="mt-5 text-center text-xs text-slate-500">
            Customer? <Link to="/login" data-testid="franchise-login-customer-link" className="font-semibold text-shipany-cyan hover:underline">Sign in at shipany.in/login</Link>
          </p>
        </div>
        <p className="mt-4 text-center text-xs text-slate-600"><Link to="/" data-testid="franchise-login-home-link" className="hover:text-shipany-cyan">← Back to shipany.in</Link></p>
      </div>
    </div>
  );
}
